import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionCallout } from '@clarion/ClarionCallout';
import { ClarionDialog, ClarionDialogFooter } from '@clarion/ClarionDialog';
import { ClarionFormGroup as FormGroup } from '@clarion/ClarionFormGroup';
import { ClarionHTMLSelect as HTMLSelect } from '@clarion/ClarionHTMLSelect';
import { ClarionTextArea } from '@clarion/ClarionTextArea';
import { ClarionTextField } from '@clarion/ClarionTextField';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useUserState } from '../auth/UserState';
import { useWorkspaceId } from '../auth/WorkspaceState';
import { getRecapFile } from '../integrations/usePartnerFile';
import { showConfirm } from '../shared/ImperativeDialog';
import { AppToaster } from '../shared/toaster';
import { cx } from '../util';
import AssigneeMultiSelect from './AssigneeMultiSelect';
import {
  Assignment,
  ASSIGNMENT_PRIORITIES,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_TYPES,
  AssignmentPriority,
  AssignmentStatus,
  AssignmentType,
  hasActiveTimer,
} from './assignment';
import {
  createNewAssignment,
  deleteAssignment,
  getFileLabels,
  updateAssignment,
} from './assignmentService';
import LabelMultiSelect from './LabelMultiSelect';
import TimeTracker from './TimeTracker';
import { useRefreshActiveTimer } from './useActiveTimer';

type AssignmentFormProps = {
  isOpen: boolean;
  onClose: () => void;
  onAssignmentCreated?: (assignment: Assignment) => void;
  onAssignmentUpdated?: (assignment: Assignment) => void;
  onAssignmentDeleted?: (assignmentId: string) => void;
  existingAssignment?: Assignment;
  extraActions?: React.ReactNode;
  fileId?: string; // For creating new assignments
};

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  isOpen,
  onClose,
  onAssignmentCreated,
  onAssignmentUpdated,
  onAssignmentDeleted,
  existingAssignment,
  extraActions,
  fileId,
}) => {
  const { user } = useUserState();
  const workspaceId = useWorkspaceId();
  const currentUserId = user?.uid || '';
  const currentUserName = user?.displayName || user?.email || 'Unknown User';
  const refreshActiveTimer = useRefreshActiveTimer();

  const { data: file } = useQuery({
    queryKey: ['recapFile', workspaceId, existingAssignment?.fileId || fileId],
    queryFn: () => getRecapFile(workspaceId, (existingAssignment?.fileId || fileId)!),
    enabled: Boolean((existingAssignment?.fileId || fileId) && workspaceId),
  });

  const { data: labels = [] } = useQuery({
    queryKey: ['labels', existingAssignment?.fileId || fileId, workspaceId],
    queryFn: () => getFileLabels((existingAssignment?.fileId || fileId)!, workspaceId),
    enabled: Boolean((existingAssignment?.fileId || fileId) && workspaceId),
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<AssignmentType>('custom');
  const [status, setStatus] = useState<AssignmentStatus>('pending');
  const [priority, setPriority] = useState<AssignmentPriority>('medium');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAssignment, setCurrentAssignment] = useState<Assignment | null>(null);

  // Initialize form with existing assignment data if editing
  useEffect(() => {
    if (existingAssignment) {
      setTitle(existingAssignment.title);
      setDescription(existingAssignment.description || '');
      setType(existingAssignment.type);
      setStatus(existingAssignment.status);
      setPriority(existingAssignment.priority);
      setDueDate(existingAssignment.dueDate ? new Date(existingAssignment.dueDate) : null);
      setAssignees(existingAssignment.assignees);
      setSelectedLabels(existingAssignment.labels || []);
      setCurrentAssignment(existingAssignment);
    } else {
      // Default values for new assignment
      setTitle('');
      setDescription('');
      setType('custom');
      setStatus('pending');
      setPriority('medium');
      setDueDate(null);
      setAssignees([]);
      setSelectedLabels([]);
      setCurrentAssignment(null);
    }
  }, [existingAssignment, isOpen]);

  const hasPendingChanges = () => {
    if (currentAssignment) {
      // For existing assignment, compare with current values
      return (
        title !== currentAssignment.title ||
        description !== (currentAssignment.description || '') ||
        type !== currentAssignment.type ||
        status !== currentAssignment.status ||
        priority !== currentAssignment.priority ||
        (dueDate?.getTime() || null) !== (currentAssignment.dueDate || null) ||
        JSON.stringify(assignees) !== JSON.stringify(currentAssignment.assignees) ||
        JSON.stringify(selectedLabels) !== JSON.stringify(currentAssignment.labels || [])
      );
    } else {
      // For new assignment, check if any field has been filled
      return (
        title.trim() !== '' ||
        description.trim() !== '' ||
        type !== 'custom' ||
        status !== 'pending' ||
        priority !== 'medium' ||
        dueDate !== null ||
        assignees.length > 0 ||
        selectedLabels.length > 0
      );
    }
  };

  const handleClose = async () => {
    if (hasPendingChanges()) {
      const confirmed = await showConfirm(
        'You have unsaved changes. Are you sure you want to discard them?',
        'Discard Changes',
        'Continue Editing',
        true,
      );

      if (!confirmed) return;
    }

    // Refresh active timers to ensure the global timer is updated
    refreshActiveTimer();

    // Call the parent component's onClose
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('File not found');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (!title.trim()) throw new Error('Title is required');
      if (!description.trim()) throw new Error('Description is required');

      if (currentAssignment) {
        const updatedAssignment = await updateAssignment(
          currentAssignment.id,
          file.workspaceId,
          {
            title,
            description: description || undefined,
            type,
            status,
            priority,
            dueDate: dueDate ? dueDate.getTime() : undefined,
            assignees,
            labels: selectedLabels.length > 0 ? selectedLabels : undefined,
          },
          currentUserId,
          currentUserName,
          'updated assignment',
        );

        onAssignmentUpdated?.(updatedAssignment);
      } else {
        const newAssignment = await createNewAssignment({
          fileId: file.id,
          workspaceId: file.workspaceId,
          title,
          type,
          userName: currentUserName,
          createdBy: currentUserId,
          assignees,
          priority,
          description,
          dueDate: dueDate?.getTime(),
          labels: selectedLabels,
        });

        onAssignmentCreated?.(newAssignment);
      }

      onClose();
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChange = (dateValue: string) => {
    setDueDate(dateValue ? new Date(dateValue) : null);
  };

  const handleDelete = async () => {
    if (!currentAssignment || !file) return;

    const confirmed = await showConfirm(
      'Are you sure you want to delete this assignment?',
      'Delete',
      'Cancel',
      true,
    );

    if (confirmed) {
      try {
        setIsSubmitting(true);
        await deleteAssignment(currentAssignment.id, file.workspaceId);

        // Signal that the assignment was deleted
        // If there's a specific onAssignmentDeleted callback, use it
        if (onAssignmentDeleted) {
          onAssignmentDeleted(currentAssignment.id);
        }
        // Otherwise use the update callback to trigger a refresh
        else if (onAssignmentUpdated) {
          // Create a dummy assignment with the ID to signal deletion
          const deletedAssignmentSignal = {
            ...currentAssignment,
            status: 'cancelled' as AssignmentStatus,
            _deleted: true, // Add a flag to indicate this assignment was deleted
          };
          onAssignmentUpdated(deletedAssignmentSignal);
        }

        // Refresh the global timer if this assignment had an active timer for the current user
        if (hasActiveTimer(currentAssignment, currentUserId)) {
          refreshActiveTimer();
        }

        AppToaster.show({
          message: 'Assignment deleted successfully',
          intent: 'success',
        });

        // Close the dialog
        onClose();
      } catch (error) {
        AppToaster.show({
          message: `Error deleting assignment: ${error instanceof Error ? error.message : String(error)}`,
          intent: 'danger',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Render only if we have currentAssignment in edit mode
  const renderTimeTracker = () => {
    if (!currentAssignment) return null;

    return (
      <FormGroup label="Time Tracking">
        <TimeTracker
          assignment={currentAssignment}
          onUpdate={(updatedAssignment) => {
            // Preserve pending changes, but update the time tracking data
            const { timeEntries, totalTimeSpent } = updatedAssignment;
            setCurrentAssignment({
              ...currentAssignment,
              title,
              description,
              type,
              status,
              priority,
              dueDate: dueDate ? dueDate.getTime() : undefined,
              assignees,
              labels: selectedLabels,
              timeEntries,
              totalTimeSpent,
            });

            if (updatedAssignment.status !== status) {
              setStatus(updatedAssignment.status);
            }

            refreshActiveTimer();

            onAssignmentUpdated?.(updatedAssignment);
          }}
        />
      </FormGroup>
    );
  };

  // Don't render if we're expecting a file but don't have one yet
  if ((existingAssignment || fileId) && !file) {
    return null;
  }

  return (
    <ClarionDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={currentAssignment ? 'Edit assignment' : 'Create assignment'}
      isDismissable={false}
      style={{ width: currentAssignment ? '900px' : '600px', maxWidth: '100vw' }}
    >
      {error && (
        <ClarionCallout intent="danger" className="mb-4 ">
          {error}
        </ClarionCallout>
      )}

      <div className={cx('grid grid-cols-1 gap-2', currentAssignment && 'md:grid-cols-2 gap-6')}>
        <div className="flex flex-col gap-2">
          <ClarionTextField
            label="Title (required)"
            placeholder="Assignment title"
            value={title}
            onChange={setTitle}
          />

          <ClarionTextArea
            label="Description (required)"
            placeholder="Assignment description"
            value={description}
            onChange={setDescription}
            fill
            autoResize
            style={{ minHeight: 80 }}
            className="font-sans"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <HTMLSelect
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as AssignmentType)}
              options={Object.entries(ASSIGNMENT_TYPES).map(([key, label]) => ({
                value: key,
                label,
              }))}
              fill
            />

            <HTMLSelect
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as AssignmentPriority)}
              options={Object.entries(ASSIGNMENT_PRIORITIES).map(([key, label]) => ({
                value: key,
                label,
              }))}
              fill
            />
          </div>

          {currentAssignment && (
            <HTMLSelect
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as AssignmentStatus)}
              options={Object.entries(ASSIGNMENT_STATUSES).map(([key, label]) => ({
                value: key,
                label,
              }))}
              fill
            />
          )}

          <ClarionTextField
            label="Due Date"
            type="date"
            value={dueDate ? dueDate.toISOString().split('T')[0] : ''}
            onChange={handleDateChange}
          />

          <FormGroup label="Assignees" labelInfo="(select one or more)">
            <AssigneeMultiSelect
              workspaceId={workspaceId}
              selectedUserIds={assignees}
              onSelectionChange={setAssignees}
            />
          </FormGroup>

          {labels.length > 0 && (
            <FormGroup label="Labels">
              <LabelMultiSelect
                availableLabels={labels}
                selectedLabelIds={selectedLabels}
                onSelectionChange={setSelectedLabels}
              />
            </FormGroup>
          )}
        </div>

        {currentAssignment && renderTimeTracker()}
      </div>

      <ClarionDialogFooter className="flex-wrap">
        <div className="flex grow gap-2">
          {extraActions}

          {currentAssignment && (
            <ClarionButton
              icon="trash"
              intent="danger"
              variant="minimal"
              onClick={handleDelete}
              loading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Delete
            </ClarionButton>
          )}
        </div>

        <ClarionButton onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </ClarionButton>

        <ClarionButton
          intent="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          isDisabled={isSubmitting}
        >
          {currentAssignment ? 'Update' : 'Create'}
        </ClarionButton>
      </ClarionDialogFooter>
    </ClarionDialog>
  );
};

export default AssignmentForm;
